import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { Delete, Edit, FileDownload } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

export default function Properties() {
  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const propsSnap = await getDocs(collection(db, 'properties'));
        const props = propsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const bankSnap = await getDocs(collection(db, 'bankAccounts'));
        const bankMap = {};
        bankSnap.forEach(doc => {
          const data = doc.data();
          bankMap[doc.id] = `${data.name} • ****${data.last4}`;
        });

        const enriched = props.map(p => ({
          ...p,
          ownersDisplay: Array.isArray(p.owners)
            ? p.owners.map(o => (typeof o === 'string' ? o : o?.name)).join(', ')
            : '',
          bankAccountName: bankMap[p.bankAccount] || p.bankAccount
        }));

        setProperties(enriched);
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Typography sx={{ m: 4 }}>Loading...</Typography>;
  if (error) return <Typography sx={{ m: 4 }}>Error loading properties.</Typography>;

  const headers = [
    { label: 'Property', key: 'name' },
    { label: 'Location', key: 'location' },
    { label: 'Rental Owners', key: 'ownersDisplay' },
    { label: 'Manager', key: 'manager' },
    { label: 'Type', key: 'type' },
    { label: 'Operating Account', key: 'bankAccountName' }
  ];

  const exportData = properties.map(p => ({
    ...p,
    location: `${p.city}, ${p.state}`
  }));

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Properties</Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => navigate('/pm/properties/add')}>
            + Add Property
          </Button>
          <CSVLink
            data={exportData}
            headers={headers}
            filename="properties_export.csv"
            style={{ textDecoration: 'none' }}
          >
            <Button variant="outlined" startIcon={<FileDownload />}>
              Export CSV
            </Button>
          </CSVLink>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Property</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Location</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Rental Owners</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Manager</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Type</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Operating Account</strong></TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.map((p) => (
              <TableRow
                key={p.id}
                hover
                sx={{ cursor: 'pointer', '& td': { py: 0.5, fontSize: '0.85rem' } }}
                onClick={() => navigate(`./${p.id}`, { state: p })}
              >
                <TableCell>
                  <Typography color="primary" sx={{ fontSize: '0.85rem' }}>
                    {p.name || `${p.street}`}
                  </Typography>
                </TableCell>
                <TableCell>{p.city}, {p.state}</TableCell>
                <TableCell>{p.ownersDisplay}</TableCell>
                <TableCell>{p.manager}</TableCell>
                <TableCell>{p.type}</TableCell>
                <TableCell>{p.bankAccountName}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton color="primary" size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton color="error" size="small">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}